import React from 'react';
import classNames from 'classnames';
import IconFont from '$/components/IconFont';
import { ToggleGroup, ToggleGroupItem } from '$/components/ToggleGroup';
import styles from './index.module.scss';

type SelectRowsValue = string | number | boolean;

export interface SelectRowsOption {
  value: SelectRowsValue;
  label: React.ReactNode;
  icon?: string;
  disabled?: boolean;
}

export interface SelectRowsProps<T extends SelectRowsOption = SelectRowsOption> {
  value?: SelectRowsValue;
  defaultValue?: SelectRowsValue;
  options: T[];
  maxItemsPerRow?: number;
  showRatioIcon?: boolean;
  disabled?: boolean;
  className?: string;
  rowClassName?: string;
  itemClassName?: string;
  labelClassName?: string;
  onValueChange?: (value: T['value'], option?: T) => void;
  renderOption?: (option: T) => React.ReactNode;
}

const OPTION_ROW_GAP = 6;

function chunkOptions<T>(options: T[], maxItemsPerRow: number): T[][] {
  const rows: T[][] = [];
  const safeSize = Math.max(1, maxItemsPerRow);

  for (let i = 0; i < options.length; i += safeSize) {
    rows.push(options.slice(i, i + safeSize));
  }

  return rows;
}

function getOptionRowStyle(maxItemsPerRow: number): React.CSSProperties {
  const safeSize = Math.max(1, maxItemsPerRow);
  const occupiedGap = (safeSize - 1) * OPTION_ROW_GAP;

  return {
    '--team-flow-select-rows-item-width': `calc(${100 / safeSize}% - ${occupiedGap / safeSize}px)`,
  } as React.CSSProperties;
}

function getRatioIconStyle(value: string, maxSize = 18): React.CSSProperties | null {
  const [widthValue, heightValue] = value.split(':').map(Number);

  if (!widthValue || !heightValue) return null;

  const ratio = widthValue / heightValue;
  const width = ratio >= 1 ? maxSize : maxSize * ratio;
  const height = ratio >= 1 ? maxSize / ratio : maxSize;

  return {
    width: `${width}px`,
    height: `${height}px`,
  };
}

function getOptionValueKey(value: SelectRowsValue) {
  return String(value);
}

export function RatioIcon({ value, size = 18 }: { value: string; size?: number; }) {
  const iconStyle = getRatioIconStyle(value, size);

  if (!iconStyle) return null;

  return (
    <span
      className={styles.ratioIconViewport}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <span className={styles.ratioIconShape} style={iconStyle} />
    </span>
  );
}

export default function SelectRows<T extends SelectRowsOption = SelectRowsOption>({
  value,
  defaultValue,
  options,
  maxItemsPerRow = 3,
  showRatioIcon = false,
  disabled = false,
  className,
  rowClassName,
  itemClassName,
  labelClassName,
  onValueChange,
  renderOption,
}: SelectRowsProps<T>) {
  const rowStyle = getOptionRowStyle(maxItemsPerRow);

  return (
    <ToggleGroup
      type="single"
      value={value !== undefined ? getOptionValueKey(value) : undefined}
      defaultValue={defaultValue !== undefined ? getOptionValueKey(defaultValue) : undefined}
      disabled={disabled}
      onValueChange={(nextValue) => {
        if (!nextValue) return;
        const option = options.find((item) => getOptionValueKey(item.value) === nextValue);
        onValueChange?.(
          (option?.value ?? nextValue) as T['value'],
          option,
        );
      }}
      className={classNames(styles.group, className)}
    >
      {chunkOptions(options, maxItemsPerRow).map((row) => (
        <div
          key={row.map((option) => getOptionValueKey(option.value)).join('-')}
          className={classNames(styles.row, rowClassName)}
          style={rowStyle}
        >
          {row.map((option) => {
            const optionValue = getOptionValueKey(option.value);

            return (
              <ToggleGroupItem
                key={optionValue}
                value={optionValue}
                disabled={option.disabled}
                className={classNames(styles.item, itemClassName)}
              >
                {renderOption ? renderOption(option) : (
                  <>
                    {option.icon ? <IconFont name={option.icon} fontSize={22} /> : null}
                    {showRatioIcon ? <RatioIcon value={optionValue} /> : null}
                    <span className={classNames(styles.label, labelClassName)}>
                      {option.label}
                    </span>
                  </>
                )}
              </ToggleGroupItem>
            );
          })}
        </div>
      ))}
    </ToggleGroup>
  );
}
